/*
 * The other half of "Show desktop notifications": actually showing one.
 *
 * The server decides *whether* a message is worth a notification and says so with `notify.hint` —
 * the decision, never the notification (protocol.md §2.5.8). Until now nothing on the client heard
 * that event: the preference shipped switched on, the browser had never been asked for permission,
 * and a granted permission would still have shown nothing (X-A4 D5). This is the listener, and the
 * settings screen is where the permission is asked for.
 *
 * Three rules, all of them about not being annoying:
 *
 *   · nothing is shown while the person is looking at the page — the conversation itself is the
 *     notification, and a banner over a window you are reading is noise;
 *   · one notification per conversation at a time (the tag), so twenty messages in a channel are
 *     one banner that updates rather than twenty banners;
 *   · the body is whatever the server was willing to send. A direct message carries no preview out
 *     of the application, ever (`outbox.go`'s `hint`), so the sentence says who wrote, not what.
 *
 * The same three rules serve a second source: an Activity row of a kind whose module asked for a
 * banner (`ActivityRowView.desktop`) — an AI chat's question, a finished request. Those rows happen
 * in no conversation, so no `notify.hint` names them; the row itself is the news. Only a *new* row
 * banners, because a raised one is something the reader was already told, unless its kind says the
 * raise is the news (an escalation). The banner's words are the feed row's own sentence and its click
 * the row's own link, so the feed and the desktop never tell one thing two ways.
 */

import type { Platform } from "../../platform/types.ts"
import { webPlatform } from "../../platform/web.ts"
import { messageHref } from "../../routes/links.ts"
import { activityBannerView } from "../activity/rowView.ts"
import { t } from "../i18n/index.ts"
import type { ModuleRegistry } from "../modules/registry.ts"
import { navigateTo } from "../shell/navigate.ts"
import { conversationAddress } from "../store/conversationName.ts"
import type { ClientStore } from "../store/store.ts"
import type { ActivityItem, NotifyHint } from "../wire/core.gen.ts"

export interface DesktopNotificationOptions {
	platform?: Platform
	document?: Document
	/** Plays the sound; the default is a short two-tone blip built with the audio API. */
	playSound?: () => void
	/**
	 * Where an activity kind's module says whether and how its rows banner. Without it only the
	 * server's `notify.hint` banners, which is every kind core itself owns.
	 */
	registry?: Pick<ModuleRegistry, "activityRow">
}

/** One banner, whichever source asked for it. */
export interface Banner {
	title: string
	body: string
	tag: string
	/** Where the click leads, as an in-application path; null leaves the window where it is. */
	href: string | null
}

/**
 * What decides whether a banner is shown at all — the account's preference, the platform's
 * permission, the window not being looked at — and, when it is, the sound. Both sources go through
 * it, so the three rules cannot drift apart between them.
 */
export function showBanner(
	store: ClientStore,
	banner: Banner,
	options: DesktopNotificationOptions = {},
): void {
	const platform = options.platform ?? webPlatform
	const doc = options.document ?? globalThis.document
	const prefs = store.model.notificationPrefs
	if (prefs !== null && !prefs.desktopEnabled) return
	if (!platform.capabilities().notify) return
	// `visible` rather than `hasFocus()`: a second window of the same application in view is still
	// the person reading, and a tab behind another window is not.
	if (doc.visibilityState === "visible") return
	void platform.notify({
		title: banner.title,
		body: banner.body,
		tag: banner.tag,
		onClick: () => {
			platform.focusWindow()
			if (banner.href !== null) navigateTo(banner.href)
		},
	})
	if (prefs === null || prefs.soundEnabled) (options.playSound ?? blip)()
}

/**
 * Starts turning notification hints, and the Activity rows whose kind asks for it, into platform
 * notifications. Returns the function that stops it; the boot installs one for the life of the
 * document, with the module registry so that a module's kinds can banner.
 */
export function installDesktopNotifications(
	store: ClientStore,
	options: DesktopNotificationOptions = {},
): () => void {
	return store.onEvent((event) => {
		if (event.type === "notify.hint") {
			const hint = event.data
			const { title, body } = contentOf(store, hint)
			showBanner(
				store,
				{ title, body, tag: `conv:${hint.conv}`, href: pathOf(store, hint) },
				options,
			)
			return
		}
		if (event.type === "activity.item" && options.registry !== undefined) {
			const banner = activityBanner(store, options.registry, event.data)
			if (banner !== null) showBanner(store, banner, options)
		}
	})
}

/**
 * The banner of one Activity row, or null when its kind asked for none, when it is a raise its kind
 * does not banner again, or when this build cannot read its payload — the feed then falls back to
 * words of its own, and a banner with no sentence would say nothing.
 */
function activityBanner(
	store: ClientStore,
	registry: Pick<ModuleRegistry, "activityRow">,
	delivery: ActivityItem,
): Banner | null {
	const { item } = delivery
	const view = activityBannerView(item.kind, item.data, registry)
	const desktop = view?.desktop
	if (view === undefined || desktop === undefined) return null
	if (!delivery.new && desktop.onRaise !== true) return null
	const actors = item.actorUserId === undefined ? [] : [item.actorUserId]
	const body = view.line(item.data, actors, (id) => store.model.users[id]?.displayName ?? "")
	if (body === "") return null
	return {
		title: desktop.title(item.data),
		body,
		tag: desktop.tag(item.data),
		href: view.href(item.data),
	}
}

/**
 * The two lines of the banner: where it happened, and what happened.
 *
 * The title is the place, in the words the rest of the product uses for a place — `#allgemein` for a
 * channel, the person for a direct conversation (`core/store/conversationName.ts`, the one naming
 * ladder) — and falls back to the name the hint carries for a conversation this client does not hold.
 * The body is what the server was willing to send: a direct message never carries its text out of the
 * application, so the sentence says who wrote rather than what. The preview arrives as JSON `null`
 * and not as an absent key, whatever the generated type says (`wire.NotifyHint.Preview` has no
 * `omitempty`), which is why it is tested for its type and not for `undefined`.
 */
function contentOf(store: ClientStore, hint: NotifyHint): { title: string; body: string } {
	const preview = typeof hint.preview === "string" && hint.preview !== "" ? hint.preview : undefined
	const author = store.model.users[hint.actorUserId]?.displayName
	const kind = store.model.conversations[hint.conv]?.meta.kind
	const direct =
		kind === undefined ? hint.conversationName === "" : kind === "dm" || kind === "groupDm"
	const place = conversationAddress(store.model, hint.conv)
	return {
		title: place || hint.conversationName || author || t("notify.desktop.title.unknown"),
		body: preview ?? sentenceOf(direct, author),
	}
}

/** What happened, when the server sent no text of it. */
function sentenceOf(direct: boolean, author: string | undefined): string {
	if (direct) return t("notify.desktop.body.direct")
	return author === undefined
		? t("notify.desktop.body.unknown")
		: t("notify.desktop.body.from", { author })
}

/** The permalink of the message that caused the hint, for the click on the banner. */
function pathOf(store: ClientStore, hint: NotifyHint): string | null {
	const meta = store.model.conversations[hint.conv]?.meta
	return meta === undefined ? null : messageHref(meta, hint.seq)
}

/** One audio context for the life of the document; six is all a browser grants. */
let audio: AudioContext | undefined

/**
 * A short two-tone blip, built rather than shipped: a sound file is another request, a licence to
 * keep and a thing to cache, and the notification sound of a chat client is two sine tones.
 *
 * A browser that has not been interacted with keeps its audio suspended. That is not an error to
 * report — the banner is already on screen — so a refusal is swallowed.
 */
function blip(): void {
	try {
		const Ctor = globalThis.AudioContext
		if (Ctor === undefined) return
		audio ??= new Ctor()
		const ctx = audio
		void ctx.resume().catch(() => undefined)
		const gain = ctx.createGain()
		gain.gain.value = 0.05
		gain.connect(ctx.destination)
		for (const [index, frequency] of [880, 1320].entries()) {
			const tone = ctx.createOscillator()
			tone.type = "sine"
			tone.frequency.value = frequency
			tone.connect(gain)
			const at = ctx.currentTime + index * 0.09
			tone.start(at)
			tone.stop(at + 0.08)
		}
	} catch {
		// No audio in this context: the notification itself is what matters.
	}
}
